/**
 * M-X36 — provider-credential status banner.
 *
 * THREE FAILURES, THREE SHAPES (2026-07-26, second pass).
 *
 * At 14:56Z this banner read "A stored provider key has stopped working" over
 * a "How to re-login" button, while Claude auth was perfectly healthy and the
 * actual fault was a dead OpenAI API key. The founder read it as his own login
 * breaking. Three separate defects, all of them wording/severity:
 *
 *   1. it never named the provider,
 *   2. it offered the Claude re-login as the remedy for an OpenAI key,
 *   3. it never said what was still working, so a one-key fault looked total.
 *
 * The fix is NOT in this file's copy. Severity, wording and remedy are decided
 * once in the gateway (claude-auth-refresher.ts: classifyDegradation →
 * combineWithProviders → buildAuthAlert/buildRemedy) and shipped in the
 * payload. This component chooses a SHAPE from `remedy.kind`; it never invents
 * a sentence the server did not send. That is deliberate: the last three bugs
 * on this surface were all two-copies-of-the-truth bugs.
 *
 *   down     → agentsBlocked: red, assertive, NOT dismissible, owner-local remedy.
 *   degraded → Claude probe failed: alert, dismissible, owner-local remedy.
 *   partial  → a provider key dead: status (not alert), dismissible, links to
 *              Settings → Credentials, and reports Claude separately without
 *              inventing health when no member-scoped probe exists.
 *
 * "Re-check now" reports WHAT it re-checked, per provider — not just a fresh
 * timestamp, which is what made a truthful button look broken.
 *
 * Polls /api/system/claude-auth every 60s. Renders nothing when
 * needsAttention=false — zero footprint in the happy path.
 *
 * English only — UI text policy.
 */
import * as React from 'react';
import {
  AlertTriangle,
  Check,
  KeyRound,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import { apiJson } from '../../lib/api/client';
import { env } from '../../env';
import { useAuthStore } from '../../stores/auth-store';
import { useIsMobile } from '../../lib/use-media-query';
import { cn } from '../../lib/utils';

interface ProviderLivenessRow {
  name: string;
  label: string;
  status: 'live' | 'dead' | 'unknown' | 'missing' | 'not_probeable';
  httpStatus: number | null;
  detail: string | null;
  checkedAt: string;
}

interface AuthStatusSummary {
  currentExpiresAt: string | null;
  hoursUntilExpiry: number | null;
  lastCheckAt: string | null;
  lastState: 'ok' | 'refreshed' | 'refresh_failed' | 'expired' | 'cli_unreachable' | null;
  recentFailures: number;
  needsAttention: boolean;
  alertSentAt: string | null;
  // 2026-07-26 — the gateway now sends ready-made plain-language copy so the
  // banner and the Telegram alert can never say different things.
  severity?: 'ok' | 'degraded' | 'down';
  agentsBlocked?: boolean;
  downForText?: string;
  headline?: string;
  detail?: string;
  reloginCommand?: string;
  providers?: ProviderLivenessRow[];
  // ── 2026-07-26 second pass ──
  claudeOk?: boolean;
  claudeStatusText?: string;
  remedy?: AuthRemedy;
  deadProviderLabels?: string[];
}

interface AuthRemedy {
  kind: 'relogin' | 'replace_key' | 'none';
  label: string;
  href: string | null;
  command: string | null;
  instruction: string;
}

interface RefreshCheckLine {
  subject: string;
  outcome: 'ok' | 'failed' | 'unknown';
  detail: string;
}

interface RefreshResponse {
  finalState: AuthStatusSummary['lastState'];
  status: AuthStatusSummary;
  checks?: RefreshCheckLine[];
  checkedScope?: 'provider_keys';
  checkedAt?: string;
}

// ⚠ 2026-07-26: 5 minutes was fine for a "telemetry" strip. This banner is now
// the on-canvas half of an outage alarm, so it polls at 60s — a founder who
// opens the tab mid-outage should not stare at a healthy-looking board for up
// to five minutes.
const POLL_INTERVAL_MS = 60 * 1000;
const DISMISS_STORAGE_KEY = 'chaos-claude-auth-banner-dismissed-at';
const DISMISS_TTL_MS = 60 * 60 * 1000; // 1 hour — banner re-appears after that.

const SUBSCRIPTION_SETTINGS_PATH = '/settings-v2?tab=ai-signins';
const FALLBACK_RELOGIN = 'Open Settings → AI sign-ins and reconnect Claude for this account.';

function readDismiss(): number | null {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeDismiss(ts: number | null): void {
  try {
    if (ts === null) localStorage.removeItem(DISMISS_STORAGE_KEY);
    else localStorage.setItem(DISMISS_STORAGE_KEY, String(ts));
  } catch {
    /* ignore */
  }
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const dt = new Date(iso).getTime();
  const diff = Date.now() - dt;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// "14:32" — local wall-clock HH:MM for the one-line polished banner.
function formatClock(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function describeState(state: AuthStatusSummary['lastState']): string {
  switch (state) {
    case 'refreshed':
      return 'refreshed successfully';
    case 'ok':
      return 'token valid';
    case 'refresh_failed':
      return 'refresh failed';
    case 'expired':
      return 'refresh token expired';
    case 'cli_unreachable':
      return 'CLI container unreachable';
    case null:
      return 'no recent checks';
    default:
      return state;
  }
}

/**
 * The remedy is the SERVER'S decision. This only covers a gateway too old to
 * send one. The fallback remains owner-local and never exposes a host command
 * or assumes that a founder-global CLI session belongs to this member.
 */
function resolveRemedy(status: AuthStatusSummary): AuthRemedy {
  if (status.remedy) return status.remedy;
  return {
    kind: 'relogin',
    label: 'Open AI sign-ins',
    href: SUBSCRIPTION_SETTINGS_PATH,
    command: null,
    instruction: FALLBACK_RELOGIN,
  };
}

/**
 * WHAT THE BUTTON ACTUALLY RE-CHECKED, per subject.
 *
 * Without this the founder pressed "refresh", was told a check ran "just now",
 * saw the banner stay, and concluded the button was broken. It was not — the
 * key really was still dead. A refresh that reports only a timestamp cannot
 * tell those two situations apart, so it must report findings instead.
 */
function RefreshResults({
  checks,
  at,
}: {
  checks: RefreshCheckLine[];
  at: string | null;
}): React.ReactElement | null {
  if (checks.length === 0) return null;
  return (
    <div
      data-testid="claude-auth-refresh-results"
      className="rounded border border-border bg-background/60 p-2.5 text-xs leading-relaxed"
    >
      <div className="mb-1.5 font-medium text-foreground/80">
        Re-checked just now{at ? ` (${formatClock(at)})` : ''}:
      </div>
      <ul className="space-y-1">
        {checks.map((line) => (
          <li key={line.subject} className="flex items-start gap-1.5">
            {line.outcome === 'ok' ? (
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
            ) : (
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span className="min-w-0">
              <span className="font-medium text-foreground/90">{line.subject}</span>
              {' — '}
              <span className="text-muted-foreground">{line.detail}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ClaudeAuthBanner(): React.ReactElement | null {
  // WHO THIS BANNER IS FOR (2026-08-05). It reports the health of the machine's
  // own AI login availability and points to the owner-local control that repairs it. Until this
  // change it was mounted for everybody in <Shell>, and its endpoint was
  // `requireAuth`, so an invited person's first screen was a red bar of ssh
  // instructions for a production box, over an empty Workspace, with a "Check
  // again" button that answered them `403 Founder only`.
  //
  // The gateway is the fence — `GET /api/system/claude-auth` is founder-only
  // now, so a non-founder gets nothing whatever this component does. This is
  // the affordance: don't ask a question we know will be refused, sixty times
  // an hour, for the whole of somebody's session.
  //
  // `undefined` means a session persisted before `systemRole` existed on
  // MeResponse. We ASK in that case rather than hiding: the server decides, and
  // the only cost of being wrong is one 403 that this component already
  // swallows. Hiding on `undefined` would blind the one person the banner is
  // for, on the one day his session was old.
  const systemRole = useAuthStore((s) => s.user?.systemRole);
  const mayOperate = systemRole === undefined || systemRole === 'founder';

  const [status, setStatus] = React.useState<AuthStatusSummary | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [lastChecks, setLastChecks] = React.useState<RefreshCheckLine[] | null>(null);
  const [lastChecksAt, setLastChecksAt] = React.useState<string | null>(null);
  const [dismissedAt, setDismissedAt] = React.useState<number | null>(() => readDismiss());
  // Wave-1 polish (F2/F3): neutral-emphasis recolour + one-line copy + a
  // mobile single-line layout. Gated so the OFF render is byte-identical.
  const uiPolish = env.VITE_UI_POLISH;
  const isMobile = useIsMobile();

  const fetchStatus = React.useCallback(async () => {
    if (!mayOperate) return;
    try {
      const data = await apiJson<AuthStatusSummary>('/system/claude-auth');
      setStatus(data);
    } catch {
      // Silently swallow — the banner is non-critical telemetry.
    }
  }, [mayOperate]);

  React.useEffect(() => {
    if (!mayOperate) return;
    void fetchStatus();
    const t = setInterval(fetchStatus, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchStatus, mayOperate]);

  const handleRefresh = React.useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const data = await apiJson<RefreshResponse>('/system/claude-auth/refresh-now', {
        method: 'POST',
      });
      setStatus(data.status);
      // Per-subject findings, not just a timestamp — see RefreshResults.
      setLastChecks(data.checks ?? null);
      setLastChecksAt(data.checkedAt ?? null);
      // If refresh worked, clear any stale dismissal so future
      // failures show the banner immediately.
      if (data.finalState === 'refreshed' || data.finalState === 'ok') {
        writeDismiss(null);
        setDismissedAt(null);
      }
    } catch {
      // Show the response from /api/system/claude-auth which the poll will pick up.
    } finally {
      setRefreshing(false);
    }
  }, [refreshing]);

  const handleDismiss = React.useCallback(() => {
    const now = Date.now();
    writeDismiss(now);
    setDismissedAt(now);
  }, []);

  // Decide whether to render.
  if (!mayOperate) return null;
  if (!status || !status.needsAttention) return null;

  // ── HARD DOWN (2026-07-26) ────────────────────────────────────────
  // agentsBlocked means no agent on this machine can start. That state is NOT
  // dismissible and NOT muted: on 2026-07-26 it lasted 10h13m and the founder
  // was never told. It renders before every other path, ignores the dismissal
  // key, and keeps the owner-local remedy on the canvas rather than exposing
  // host/container commands.
  const remedy = resolveRemedy(status);
  const relogin = remedy.instruction || FALLBACK_RELOGIN;
  const deadProviders = (status.providers ?? []).filter((p) => p.status === 'dead');
  const liveProviders = (status.providers ?? []).filter((p) => p.status === 'live');
  const refreshResults =
    lastChecks && lastChecks.length > 0 ? (
      <RefreshResults checks={lastChecks} at={lastChecksAt} />
    ) : null;
  if (status.agentsBlocked) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        data-testid="claude-auth-banner-down"
        className={cn(
          'flex flex-col gap-2 border-b-2 border-destructive/60 bg-destructive/10',
          'px-3 py-2.5 text-sm text-destructive md:px-4',
        )}
      >
        <div className="flex min-w-0 items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">
              {status.headline ?? 'Agents cannot run — Claude login expired'}
            </div>
            <div className="mt-0.5 text-xs text-destructive/90">
              {status.detail ??
                'This account cannot start an agent. Reconnect it in Settings → AI sign-ins.'}
            </div>
            {deadProviders.length > 0 && (
              <div className="mt-0.5 text-xs text-destructive/80">
                Also failing:{' '}
                {deadProviders.map((p) => `${p.label} (${p.httpStatus ?? 'error'})`).join(', ')}
              </div>
            )}
            <pre className="mt-2 overflow-x-auto whitespace-pre rounded border border-destructive/30 bg-background/70 p-2 font-mono text-xs text-foreground/90">
              {relogin}
            </pre>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded border border-destructive/40',
              'bg-destructive/10 px-2.5 py-1 text-xs font-medium',
              'hover:bg-destructive/20 disabled:opacity-60',
            )}
          >
            <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} aria-hidden />
            {refreshing ? 'Checking…' : 'Check again'}
          </button>
        </div>
        {refreshResults}
      </div>
    );
  }

  if (dismissedAt !== null && Date.now() - dismissedAt < DISMISS_TTL_MS) return null;

  // ── PARTIAL: a stored API key is dead; Claude is reported separately ──
  // The 14:56Z banner. Rendered BEFORE the VITE_UI_POLISH split on purpose:
  // one shape for both flag states, so this copy cannot drift the way the
  // legacy Claude branch did. Three things the old banner did not do — it
  // NAMES the provider (in the gateway's headline), it says out loud that
  // Claude's independently-known status, and its primary action is the one that
  // actually fixes a dead API key.
  //
  // role="status", not "alert": one dead key is not a total outage, and the
  // severity ladder has to be real for a screen reader too.
  if (remedy.kind === 'replace_key') {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="claude-auth-banner-provider"
        className={cn(
          'flex flex-col gap-2 border-b border-border bg-foreground/[0.03]',
          'px-3 py-2 text-sm text-foreground md:px-4',
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 font-medium">
            {status.headline ?? 'A stored provider key has stopped working'}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            {remedy.href && (
              /* A plain anchor, not a router <Link>: this component is rendered
                 standalone by the browser proof, where no router context
                 exists. A full navigation to an in-app path is correct and
                 costs one reload. */
              <a
                href={remedy.href}
                data-testid="claude-auth-fix-link"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded border border-border',
                  'bg-foreground/[0.04] px-2.5 py-1 text-xs font-medium no-underline',
                  'hover:bg-foreground/[0.08]',
                )}
              >
                <KeyRound className="h-3 w-3" aria-hidden />
                {remedy.label}
              </a>
            )}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className={cn(
                'inline-flex items-center gap-1.5 rounded border border-border',
                'px-2.5 py-1 text-xs hover:bg-foreground/[0.06] disabled:opacity-60',
              )}
            >
              <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} aria-hidden />
              {refreshing ? 'Re-checking…' : 'Re-check now'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="rounded p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>
        {/* WHAT IS STILL WORKING. The founder's actual question at 14:56Z was
            "did my authorisation drop again?" — answer it on the canvas. */}
        <div className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
          <Check className="h-3 w-3 shrink-0" aria-hidden />
          <span data-testid="claude-auth-still-working">
            {status.claudeStatusText ??
              'Claude status is unavailable until a member-scoped probe exists.'}
            {liveProviders.length > 0 &&
              ` ${liveProviders.map((p) => p.label).join(', ')} still work.`}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">{remedy.instruction}</div>
        {refreshResults}
      </div>
    );
  }

  const expiryText =
    status.hoursUntilExpiry !== null
      ? status.hoursUntilExpiry < 0
        ? `expired ${Math.abs(status.hoursUntilExpiry).toFixed(1)}h ago`
        : `expires in ${status.hoursUntilExpiry.toFixed(1)}h`
      : 'expiry unknown';

  // ── Path B: Wave-1 polished banner (F2 neutral + one-line copy, F3 mobile) ──
  if (uiPolish) {
    // One plain on-canvas line; machine detail moves behind "How to re-login".
    // Prefer the gateway's plain-language headline (single source of truth,
    // shared with the Telegram alert) and keep the clock as the suffix.
    const headline = status.headline
      ? `${status.headline} — last checked ${formatClock(status.lastCheckAt)}`
      : `Claude needs re-auth — last checked ${formatClock(status.lastCheckAt)}`;
    return (
      <div
        role="alert"
        aria-live="polite"
        data-testid="claude-auth-banner-degraded"
        className={cn(
          // Neutral-emphasis: ink text on a faint neutral fill + a neutral
          // border. No cherry/red anywhere (status = word + neutral).
          'flex flex-col gap-2 border-b border-border bg-foreground/[0.04]',
          'px-3 py-2 text-sm text-foreground md:px-4',
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          {/* One line: icon + truncated title (min-w-0 + truncate). */}
          <span className="min-w-0 flex-1 truncate font-medium">{headline}</span>
          {isMobile ? (
            // F3: collapse the two actions into ONE control on mobile. The
            // single button opens the help (which carries the manual re-login
            // and the detail copy); ≤2 lines total, full-width.
            <button
              type="button"
              onClick={() => setHelpOpen((v) => !v)}
              aria-expanded={helpOpen}
              className={cn(
                'inline-flex shrink-0 items-center gap-1 rounded border border-border',
                'px-2.5 py-1 text-xs font-medium hover:bg-foreground/[0.06]',
              )}
            >
              {helpOpen ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
              Fix
            </button>
          ) : (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded border border-border',
                  'bg-foreground/[0.04] px-2.5 py-1 text-xs font-medium',
                  'hover:bg-foreground/[0.08] disabled:opacity-60',
                )}
              >
                <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} aria-hidden />
                {refreshing ? 'Refreshing…' : 'Try refresh now'}
              </button>
              <button
                type="button"
                onClick={() => setHelpOpen((v) => !v)}
                className={cn(
                  'inline-flex items-center gap-1 rounded border border-border',
                  'px-2.5 py-1 text-xs hover:bg-foreground/[0.06]',
                )}
                aria-expanded={helpOpen}
              >
                {helpOpen ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
                Open AI sign-ins
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss"
                className="rounded p-1 text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          )}
        </div>
        {helpOpen && (
          <div className="rounded border border-border bg-background/60 p-3 text-xs leading-relaxed text-muted-foreground">
            {/* Machine copy lives HERE now, not on the canvas line. */}
            <div className="mb-2 font-sans text-foreground/80">
              {describeState(status.lastState)} · {expiryText} · last check {relativeTime(status.lastCheckAt)}
            </div>
            {isMobile && (
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className={cn(
                  'mb-2 inline-flex w-full items-center justify-center gap-1.5 rounded border border-border',
                  'bg-foreground/[0.04] px-2.5 py-1.5 text-xs font-medium',
                  'hover:bg-foreground/[0.08] disabled:opacity-60',
                )}
              >
                <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} aria-hidden />
                {refreshing ? 'Refreshing…' : 'Try refresh now'}
              </button>
            )}
            <div className="mb-1.5 font-sans">
              Reconnect the executing account in Settings → AI sign-ins:
            </div>
            <pre className="overflow-x-auto whitespace-pre font-mono text-foreground/80">
              {relogin}
            </pre>
          </div>
        )}
        {refreshResults}
      </div>
    );
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="claude-auth-banner-degraded"
      className={cn(
        'flex flex-col gap-2 border-b border-destructive/40 bg-destructive/5',
        'px-4 py-2 text-sm text-destructive',
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="flex-1 min-w-0">
          {/* Same plain-language headline as the polished path and the
              Telegram alert. Before 2026-07-26 this branch hardcoded "Claude
              subscription needs re-auth" — a different sentence for the same
              event, which is how two surfaces start telling different stories.
              Production runs VITE_UI_POLISH=true, but a reachable branch that
              says something else is a lie waiting for a flag flip. */}
          <div className="font-medium">
            {status.headline ?? 'Claude subscription needs re-auth'}
          </div>
          <div className="text-xs text-destructive/80">
            {status.detail ??
              `${describeState(status.lastState)} · ${expiryText} · last check ${relativeTime(status.lastCheckAt)}`}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className={cn(
              'inline-flex items-center gap-1.5 rounded border border-destructive/40',
              'bg-destructive/10 px-2.5 py-1 text-xs font-medium',
              'hover:bg-destructive/15 disabled:opacity-60',
            )}
          >
            <RefreshCw className={cn('h-3 w-3', refreshing && 'animate-spin')} aria-hidden />
            {refreshing ? 'Refreshing…' : 'Try refresh now'}
          </button>
          <button
            type="button"
            onClick={() => setHelpOpen((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1 rounded border border-destructive/30',
              'px-2.5 py-1 text-xs hover:bg-destructive/10',
            )}
            aria-expanded={helpOpen}
          >
            {helpOpen ? <ChevronUp className="h-3 w-3" aria-hidden /> : <ChevronDown className="h-3 w-3" aria-hidden />}
            Open AI sign-ins
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss"
            className="rounded p-1 text-destructive/70 hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </div>
      {helpOpen && (
        <div className="ml-6 rounded border border-destructive/20 bg-background/60 p-3 font-mono text-xs leading-relaxed text-foreground/80">
          <div className="mb-1.5 text-xs font-sans text-muted-foreground">
            Reconnect the executing account in Settings → AI sign-ins:
          </div>
          <pre className="overflow-x-auto whitespace-pre">{relogin}</pre>
        </div>
      )}
      {refreshResults}
    </div>
  );
}
